/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css } from 'aphrodite';
import { colors, Markup, Modal } from '@blend/consumer-components';

import { zIndex as zIndexes } from '../../../../style/zIndex';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { getStyleConfig, styles } from '../styles';
import { ActionButton } from '@components/ActionButton/ActionButton';

export namespace ConfirmationModal {
  export interface ContentProps extends React.HTMLAttributes<HTMLDivElement> {
    heading: string;
    description?: string;
    confirmButtonText: string;
    closeButtonText?: string;
    isForm?: boolean;
    handleClose?(): void;
    handleConfirm(): void;
    isDisabled?: boolean;
    buttonColor?: string;
  }

  export interface Props extends ContentProps {
    isOpen: boolean;
    contentLabel: string;
  }
}

export const ConfirmationModalContent: React.FC<ConfirmationModal.ContentProps> = ({
  handleConfirm,
  handleClose,
  confirmButtonText,
  heading,
  description,
  closeButtonText,
  isForm,
  isDisabled,
  buttonColor,
  children,
}) => {
  const onConfirm = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    handleConfirm();
  };

  const onClose = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // We assume this is defined here because the button this handler is attached to should not be rendered
    // if handleClose is undefined
    handleClose!();
  };

  return (
    <div className={css(styles.content)}>
      <h1 className={css(styles.heading)}>{heading}</h1>
      <p className={css(styles.description)}>{description ? <Markup content={description} /> : description}</p>
      {children}
      <div className={css(styles.primaryButton)}>
        <ActionButton
          data-dd-privacy="allow"
          onClick={onConfirm}
          disabled={isDisabled}
          data-it="ConfirmationModal-confirm"
          type={isForm ? 'submit' : 'button'}
          color={buttonColor ? buttonColor : colors.catalyst}
        >
          {confirmButtonText}
        </ActionButton>
      </div>
      {handleClose && (
        <ActionButton
          data-dd-privacy="allow"
          option={'secondary'}
          onClick={onClose}
          disabled={isDisabled}
          data-it="ConfirmationModal-cancel"
          color={buttonColor ? buttonColor : colors.catalyst}
        >
          {closeButtonText}
        </ActionButton>
      )}
    </div>
  );
};

export const ConfirmationModal: React.FC<ConfirmationModal.Props> = ({ isOpen, contentLabel, ...contentProps }) => {
  const isMobileView = useRootSelector(state => state.layout.isMobileView);
  return (
    <Modal
      isOpen={isOpen}
      contentLabel={contentLabel}
      handleClose={contentProps.handleClose ? contentProps.handleClose : () => {}}
      styleConfig={getStyleConfig(isMobileView)}
      overlayStyleConfig={{ zIndex: zIndexes.modal }}
      data-it="ConfirmationModal"
    >
      <ConfirmationModalContent {...contentProps} />
    </Modal>
  );
};
